import moment from "moment";
import addCommonFormats from "ajv-formats";
import NP from "number-precision";
import ERROR_MESSAGE_COPIES from "@/constants/error-messages";

const addFormats = (ajv) => {
	addCommonFormats(ajv);
	// `x-allOf`: custom conditional (if/then) keyword authored in admin-ui and
	// enforced by the Go producer (dex-monorepo dex_validator.go), NOT in the UI.
	// Registered here as a no-op vocabulary keyword so the eform's strict-mode
	// ajv.compile() tolerates it instead of throwing "unknown keyword: x-allOf".
	// `x-customValidators`: cross-field custom validators emitted by the producer
	// (dex-monorepo / admin-corev2) and enforced server-side, NOT in the UI.
	// Registered as a no-op vocabulary keyword so the eform's strict-mode
	// ajv.compile() tolerates it instead of throwing "unknown keyword: x-customValidators".
	// `x-autoFillRules`: auto-fill rules authored in admin-ui and consumed by the
	// UI's useAutoFillRules hook / eform-schema-paths.js getAutoFillRules (CTD-10784);
	// it is not stripped before compile. Registered as a no-op vocabulary keyword so
	// the eform's strict-mode ajv.compile() tolerates it instead of throwing
	// "unknown keyword: x-autoFillRules".
	// `x-uenCompare`: Contributor UEN comparison rule as authored in admin-ui. Like
	// `x-allOf` and `x-customValidators` above, the `x-` form is NOT enforced in the UI;
	// it is registered as a no-op so strict-mode compile tolerates it. Only the stored,
	// stripped `uenCompare` form is enforced — see the addKeyword registration below.
	// Enforcing both would double-fire on a schema carrying both forms, producing two
	// identical errors on the same field.
	// `submissionContext`: submission context injected onto the root schema by the
	// submission channels before compile (same pattern as `masterData`), read by the
	// `uenCompare` keyword (CTD-12664). Registered as a no-op vocabulary keyword so the
	// eform's strict-mode ajv.compile() tolerates it. `uenCompare` is NOT listed here —
	// it is a real validating keyword registered via addKeyword below.
	ajv.addVocabulary(['example', 'x-uitype', 'x-uioptions', 'x-uidependencies', 'masterDataMappings', 'masterData', 'securityClassification', 'sensitivityClassification', 'x-allOf', 'x-customValidators', 'x-autoFillRules', 'x-uenCompare', 'submissionContext']);
	ajv.addFormat("custom:date-time:YYYY-MM-DD HH:mm:ss", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM-DD HH:mm:ss", true).isValid();
		},
	});
	ajv.addFormat("custom:date-time:YYYY-MM-DDTHH:mm:ssZ", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM-DDTHH:mm:ssZ", true).isValid();
		},
	});

	ajv.addFormat("custom:date-time:YYYY-MM", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM", true).isValid();
		},
	});

	ajv.addFormat("custom:date-only:YYYY-MM-DD", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM-DD", true).isValid();
		},
	});

	//To handle decimal precision correctly
	ajv.removeKeyword("multipleOf");
	ajv.addKeyword({
		keyword: "multipleOf",
		type: "number",
		compile(schema) {
			return (data) => Number.isInteger(NP.divide(data, schema));
		},
		errors: false,
		metaSchema: {
			type: "number",
		},
	});

	// Adding Business Logic validation rules
    // Sum of fields at root level and field inside an array of objects equals to given number
	ajv.addKeyword({
		keyword: 'sumEquals',
		type: 'object',
		schemaType: 'object',
		errors: true,
		validate: function validateSumEquals(schema, data) {
			const errors = [];
			const rootSum = (schema.fields || []).reduce(
				(acc, key) => acc + (data[key] || 0),
				0
			);
			const arraySum = Array.isArray(data[schema.arrayField])
				? data[schema.arrayField].reduce((acc, item) => {
					return acc + (item[schema.arrayFieldProp] || 0);
				}, 0)
				: 0;

			const total = rootSum + arraySum;
			const isValid = total === schema.total;
			if (!isValid) {
				// Add errors for each root-level field
				for (const field of schema.fields) {
					errors.push({
						keyword: 'sumEquals',
						instancePath: `/${field}`,
						schemaPath: '',
						params: { rootSum, arraySum, total, totalExpected: schema.total },
						message: `The total ratio must equal to ${schema.total}`,
					});
				}
	  			// Add errors for each array item
				if (Array.isArray(data[schema.arrayField])) {
					for (let i = 0; i < data[schema.arrayField].length; i++) {
						errors.push({
							keyword: 'sumEquals',
							instancePath: `/${schema.arrayField}/${i}/${schema.arrayFieldProp}`,
							schemaPath: '',
							params: { rootSum, arraySum, total, totalExpected: schema.total },
							message: `The total ratio must equal to ${schema.total}`,
						});
					}
				}
				validateSumEquals.errors = errors;
				return false;
			}
			return isValid;
		},
	});

	//  Validate dates and ensure they are in chronological order
	ajv.addKeyword({
		keyword: 'dateCompare',
		type: 'object',
		schemaType: 'array',
		errors: true,

		validate: function validateDateCompare(schema, data) {
			// Map technical operators to grammatically correct phrases
			const operatorPhrases = {
				"lessThan": "before",
				"lessThanOrEqual": "on or before",
				"greaterThan": "after",
				"greaterThanOrEqual": "on or after",
				"sameDate": "on the same day as"
			};

			// Helper function to get grammatically correct phrase for an operator
            const getOperatorPhrase = (operator) => operatorPhrases[operator] || operator;

			// Helper function to create error object
			const createDateCompareError = (rule, message, index=null) => ({
				keyword: 'dateCompare',
				instancePath: rule.rootField ? `/${rule.rootField}` : `${rule.arrayField}/${index}/${rule.before}`,
				schemaPath: '',
				params: {
					rootField: rule.rootField,
					itemField: rule.itemField,
					operator: getOperatorPhrase(rule.operator)
				},
				message
			});

			// Helper function to validate date comparison based on operator
			const validateComparison = (beforeField, afterField, operator) => {
				const beforeDate = moment.parseZone(beforeField);
				const afterDate = moment.parseZone(afterField);

				if (!beforeDate.isValid() || !afterDate.isValid()) {
					return false;
				}

				// Map operators to moment methods for cleaner code
				const operatorMap = {
					"lessThan": () => beforeDate.isBefore(afterDate),
					"lessThanOrEqual": () => beforeDate.isSameOrBefore(afterDate),
					"greaterThan": () => beforeDate.isAfter(afterDate),
					"greaterThanOrEqual": () => beforeDate.isSameOrAfter(afterDate),
					"sameDate": () => beforeDate.isSame(afterDate, 'day')
				};

				// Return false for unknown operators
				return operatorMap[operator] ? operatorMap[operator]() : false;
			};

			// Helper function to validate root level date comparison
			const validateRootComparison = (rule, data) => {
				const valid = validateComparison(data[rule.before], data[rule.after], rule.operator);
				console.log("valid", valid)
				if (!valid) {
					const phrase = getOperatorPhrase(rule.operator);
					validateDateCompare.errors = [createDateCompareError(
						rule, 
						`Date ${data[rule.before]} should be ${phrase} ${data[rule.after]}`
                    )];
				}
                return valid;
			};

			// Helper function to validate array level date comparison
			const validateArrayComparison = (rule, data) => {
				const arrayField = data[rule.arrayField];

				if (!Array.isArray(arrayField)) {
					validateDateCompare.errors = [createDateCompareError(
						rule,
						`Invalid rule configuration for date comparison`
					)];
					return false;
				}
				let index=0;
				validateDateCompare.errors=[];
				let error=false;
				for (const item of arrayField) {
					const valid = validateComparison(item[rule.before], item[rule.after], rule.operator);
					if (!valid) {
						const phrase = getOperatorPhrase(rule.operator);
						validateDateCompare.errors.push(createDateCompareError(
							rule,
							`Date ${item[rule.before]} should be ${phrase} ${item[rule.after]}`,
							index
						));
						error=true;
					}
					index++;
				}

				return !error;
			};

			// Main validation logic
			for (const rule of schema) {
				// Validate rule has a type
				if (!rule.type) {
					validateDateCompare.errors = [createDateCompareError(
						rule,
						`Invalid rule configuration for date comparison`
					)];
					return false;
				}

				// Process based on comparison type
				if (rule.type === "ROOT") {
					if (!validateRootComparison(rule, data)) {
						return false;
					}
				} else if (rule.type === "ARRAY") {
					if (!validateArrayComparison(rule, data)) {
						return false;
					}
				} else {
					// Unknown comparison type
					validateDateCompare.errors = [createDateCompareError(
						rule,
						`Unknown comparison type: ${rule.type}`
					)];
					return false;
				}
			}

			return true;
		},
	});

	// Validate same day at root level and fields inside array
	ajv.addKeyword({
		keyword: "sameDay",
		type: "object",
		schemaType: "object",
		errors: true,
		validate: function validateSameDay(schema, data) {
			const errors = [];
			const rootDateStr = data[schema.rootField];
			const rootDate = moment.parseZone(rootDateStr, 'YYYY-MM-DD', true);
			const array = data[schema.arrayField];

			if (!rootDate.isValid() || !Array.isArray(array)) return true;

			array.forEach((item, index) => {
				const itemDateStr = item[schema.itemField];
				const itemDate = moment.parseZone(itemDateStr);
				if (!itemDate.isValid() || !itemDate.isSame(rootDate, 'day')) {
					errors.push({
						keyword: 'sameDay',
						instancePath: `/${schema.rootField}`,
						schemaPath: '',
						params: {
							rootDate: rootDateStr,
							itemDate: itemDateStr,
							rootField: schema.rootField,
							itemField: schema.itemField
						},
						message: `Date of ${schema.itemField} must be same day as the ${schema.rootField}.`
					});
					errors.push({
						keyword: 'sameDay',
						instancePath: `/${schema.arrayField}/${index}/${schema.itemField}`,
						schemaPath: '',
						params: {
							rootDate: rootDateStr,
							itemDate: itemDateStr,
							rootField: schema.rootField,
							itemField: schema.itemField
						},
						message: `Date of ${schema.itemField} must be same day as the ${schema.rootField}.`
					});
				}
			});
			if (errors.length > 0) {
				validateSameDay.errors = errors;
				return false;
			}

			return true;
  		}
	});

	// Validate max day duration
	ajv.addKeyword({
		keyword: "maxTotalDuration",
		type: "object",
		schemaType: "object",
		errors: true,
		validate: function validateMaxTotalDuration(schema, data) {
			const array = data[schema.arrayField];
			const timeInField = schema.timeInField;
			const timeOutField = schema.timeOutField;
			const maxHours = schema.maxHours;

			if (!Array.isArray(array)) return true;

			let totalMinutes = 0;

			for (const entry of array) {
				const inTime = moment.parseZone(entry[timeInField]);
				const outTime = moment.parseZone(entry[timeOutField]);
				if (!inTime.isValid() || !outTime.isValid()) continue;

				const diffMinutes = outTime.diff(inTime, 'minutes');
				if (diffMinutes < 0) continue; // skip if invalid range

				totalMinutes += diffMinutes;
			}

			const totalHours = totalMinutes / 60;
			const valid = totalHours <= maxHours;

			if (!valid) {
				validateMaxTotalDuration.errors = [{
					keyword: 'maxTotalDuration',
					instancePath: `/${schema.arrayField}`,
					schemaPath: '',
					params: {
						totalHours,
						maxHours,
						arrayField: schema.arrayField
					},
					message: `Total hours within ${schema.arrayField} cannot exceed ${maxHours} hours`
				}];
			}

			return valid;
		}
	});

	ajv.addKeyword({
		keyword: 'masterDataValidation',
		type: 'object',
		schemaType: 'object',
		errors: true,
		validate: function validateMasterData(mappings, payload, fullSchema) {
			const masterData = fullSchema?.masterData;
			
			if (!mappings || Object.keys(mappings).length === 0) {
				return true;
			}

			if (!masterData) {
				validateMasterData.errors = [{
					keyword: 'masterDataValidation',
					instancePath: '',
					schemaPath: '',
					params: {},
					message: 'Master data mappings defined but master data is missing'
				}];
				return false;
			}

			const errors = [];

			for (const [fieldName, fieldConfig] of Object.entries(mappings)) {
				if (!fieldConfig.masterDataTableName || !fieldConfig.masterDataFieldName) {
					continue;
				}

				const validValues = getValidValuesFromMasterData(masterData, fieldConfig.masterDataTableName, fieldConfig.masterDataFieldName);

				if (validValues.size === 0) continue;

				if (isArrayField(fieldConfig)) {
					validateArrayField(payload, fieldName, fieldConfig, validValues, errors);
				} else {
					validateRootField(payload, fieldName, fieldConfig, validValues, errors);
				}
			}

			if (errors.length > 0) {
				validateMasterData.errors = errors;
				return false;
			}
			return true;


			function getValidValuesFromMasterData(masterData, tableName, fieldName) {
				const table = masterData[tableName];
				if (!Array.isArray(table)) return new Set();
				
				return new Set(
					table
						.map(row => row[fieldName])
						.filter(val => val != null && val !== '')
						.map(String)
				);
			}

			function isArrayField(config) {
				return config.payloadParentType === 'array' && config.payloadParentField;
			}

			function validateArrayField(payload, fieldName, config, validValues, errors) {
				const array = payload[config.payloadParentField];
				
				if (!Array.isArray(array)) return;

				array.forEach((item, index) => {
					if (!item || typeof item !== 'object') return;
					
					const value = item[fieldName];
					if (value == null || value === '') return;

					if (!validValues.has(String(value))) {
						errors.push({
							keyword: 'masterDataValidation',
							instancePath: `/${config.payloadParentField}/${index}/${fieldName}`,
							schemaPath: '',
							params: {
								field: fieldName,
								value: String(value),
								masterDataTable: config.masterDataTableName,
								masterDataField: config.masterDataFieldName
							},
							message: 'Value is not valid according to master data',
							data: value
						});
					}
				});
			}

			function validateRootField(payload, fieldName, config, validValues, errors) {
				const value = payload[fieldName];
				
				if (value == null || value === '') return;

				if (!validValues.has(String(value))) {
					errors.push({
						keyword: 'masterDataValidation',
						instancePath: `/${fieldName}`,
						schemaPath: '',
						params: {
							field: fieldName,
							value: String(value),
							masterDataTable: config.masterDataTableName,
							masterDataField: config.masterDataFieldName
						},
						message: 'Value is not valid according to master data',
						data: value
					});
				}
			}
		}
	});

	// Compare payload UEN fields against the Contributor UEN for the submission (CTD-12664).
	// Schema rule: uenCompare: { sourceRef: "onBehalfOfId", targetFields: ["field", ...] }.
	// Submission channels inject the resolved context onto the ROOT schema before compile
	// (same pattern as masterData): schema.submissionContext = { onBehalfOfId: <Contributor UEN> }.
	//
	// Contract, in precedence order:
	//   no submissionContext anywhere         -> inert (channel not integrated; read modes,
	//                                            package views etc. are unaffected)
	//   sourceRef key absent, or null         -> inert (no Contributor nominated for this
	//                                            submission — channels must omit the key or
	//                                            pass null for self-submissions)
	//   sourceRef present but blank/whitespace-> "identifier not configured" error
	//   targetField undeclared in schema AND
	//     absent from the payload             -> "rule misconfigured" error (fails CLOSED: a
	//                                            typo'd or nested field name would otherwise
	//                                            let a mismatched UEN through silently)
	//   targetField declared but absent/empty -> skipped; the DE's required-field validation
	//                                            governs the outcome
	//   targetField fails its own pattern/
	//     minLength/maxLength constraints     -> skipped; the field's format validation
	//                                            governs the outcome (a mismatch error on a
	//                                            malformed value would bury the actionable
	//                                            format message, e.g. mid-typing)
	//   mismatch                              -> error anchored at the offending field
	//
	// Comparison is an opaque string match: trimmed, case-insensitive, no UEN format parsing.
	// Errors never disclose the expected UEN in `message` or `params`.
	//
	// Registered via `compile` rather than `validate` so the rule can (a) read
	// submissionContext from the ROOT schema and (b) prefix errors with dataCxt.instancePath.
	// A rule nested inside an array item therefore still resolves the context and anchors to
	// /<array>/<index>/<field> instead of collapsing every row onto /<field>.
	// No `type`/`schemaType` on purpose: declaring them makes ajv strict mode log
	// `missing type "object"` on every compile of a schema without an explicit type, and a
	// malformed rule value must no-op rather than throw and blank the eform for that DE.
	const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

	// One checker per target field that declares its own string constraints,
	// mirroring ajv semantics: unanchored pattern with the `u` flag, lengths in
	// Unicode code points. A pattern RegExp that cannot construct disables that
	// field's checker (ajv's own `pattern` compile is the one that reports it;
	// the uenCompare keyword never throws).
	// Unlike the keyword's undeclared-field check, this needs no composition
	// guard: composition only makes a field's ABSENCE from `properties`
	// untrustworthy — constraints on a PRESENT field apply regardless.
	const buildFieldFormatCheckers = (targetFields, props) => {
		const checkers = {};
		if (props == null || typeof props !== 'object') return checkers;

		for (const field of targetFields) {
			const fieldSchema = props[field];
			if (fieldSchema == null || typeof fieldSchema !== 'object') continue;

			let pattern = null;
			if (typeof fieldSchema.pattern === 'string') {
				try { pattern = new RegExp(fieldSchema.pattern, 'u'); } catch { pattern = null; }
			}
			const { minLength, maxLength } = fieldSchema;
			if (pattern == null && typeof minLength !== 'number' && typeof maxLength !== 'number') continue;

			checkers[field] = (value) => {
				const length = Array.from(value).length;
				if (typeof minLength === 'number' && length < minLength) return false;
				if (typeof maxLength === 'number' && length > maxLength) return false;
				return pattern == null || pattern.test(value);
			};
		}
		return checkers;
	};
	ajv.addKeyword({
		keyword: 'uenCompare',
		errors: true,
		compile(rule, parentSchema, it) {
			const rootSchema = it?.schemaEnv?.root?.schema;
			const targetFields = Array.isArray(rule?.targetFields)
				? rule.targetFields.filter((f) => typeof f === 'string' && f !== '')
				: [];
			const sourceRef = typeof rule?.sourceRef === 'string' ? rule.sourceRef : '';
			const isUsable = targetFields.length > 0 && sourceRef !== '';

			// Warned once per compile (not per validation) so a malformed rule is discoverable
			// rather than silently inert — the keyword deliberately does not throw here.
			if (!isUsable) {
				console.warn('uenCompare: rule ignored, expected { sourceRef: string, targetFields: string[] }');
			}

			// A `properties` check is only trustworthy when this schema level declares
			// properties and does not compose branches that could legitimately hold the field.
			const props = parentSchema?.properties;
			const composes = ['oneOf', 'anyOf', 'allOf', '$ref'].some((k) => parentSchema?.[k] !== undefined);
			const canCheckSchema = !composes && props != null && typeof props === 'object';
			const undeclared = canCheckSchema ? targetFields.filter((f) => !hasOwn(props, f)) : [];
			if (undeclared.length > 0) {
				console.warn(`uenCompare: targetFields not declared in this schema: ${undeclared.join(', ')}`);
			}

			const fieldFormatCheckers = buildFieldFormatCheckers(targetFields, props);

			return function validateUenCompare(data, dataCxt) {
				validateUenCompare.errors = null;
				if (!isUsable) return true;
				if (data == null || typeof data !== 'object' || Array.isArray(data)) return true;

				const context = parentSchema?.submissionContext ?? rootSchema?.submissionContext;
				if (context == null || typeof context !== 'object') return true;

				// hasOwn, not `in`: `sourceRef: "toString"` must not resolve up the prototype chain
				const rawSource = hasOwn(context, sourceRef) ? context[sourceRef] : undefined;
				if (rawSource == null) return true;

				const normalize = (value) => String(value).trim().toUpperCase();
				const sourceUen = normalize(rawSource);
				const basePath = dataCxt?.instancePath || '';
				const errors = [];

				if (sourceUen === '') {
					// Submission-level problem — one error covers every target field
					validateUenCompare.errors = [{
						keyword: 'uenCompare',
						instancePath: basePath,
						schemaPath: '',
						params: { sourceRef, reason: 'contributorIdentifierNotConfigured' },
						message: ERROR_MESSAGE_COPIES.business?.uenCompareNotConfigured
							|| "The Contributor's identifier is not configured for this submission.",
						data,
					}];
					return false;
				}

				for (const field of targetFields) {
					const rawValue = hasOwn(data, field) ? data[field] : undefined;

					// Undeclared in the schema AND missing from the payload: the rule names a field
					// that does not exist here, so there is nothing to compare and skipping would
					// silently pass a record this keyword exists to catch.
					if (rawValue === undefined && undeclared.includes(field)) {
						errors.push({
							keyword: 'uenCompare',
							instancePath: basePath,
							schemaPath: '',
							params: { field, sourceRef, reason: 'targetFieldNotInSchema' },
							message: ERROR_MESSAGE_COPIES.business?.uenCompareRuleMisconfigured
								|| 'This submission could not be validated against the Contributor. Please contact support.',
							data,
						});
						continue;
					}

					const submitted = rawValue == null ? '' : normalize(rawValue);
					if (submitted === '') continue;

					// See the contract above: a malformed value belongs to the field's own
					// format validation, so it is not compared. Strings only — ajv applies
					// pattern/length keywords only to strings.
					const meetsOwnFormat = fieldFormatCheckers[field];
					if (typeof rawValue === 'string' && meetsOwnFormat && !meetsOwnFormat(rawValue)) continue;

					if (submitted !== sourceUen) {
						errors.push({
							keyword: 'uenCompare',
							instancePath: `${basePath}/${field}`,
							schemaPath: '',
							params: { field, sourceRef, reason: 'uenMismatch' },
							message: ERROR_MESSAGE_COPIES.business?.uenCompareMismatch
								|| 'The UEN provided does not match the Contributor for this submission.',
							data: rawValue,
						});
					}
				}

				if (errors.length > 0) {
					validateUenCompare.errors = errors;
					return false;
				}
				return true;
			};
		},
	});
};

export default addFormats;
